import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Lock, ChevronLeft, Package, Truck } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import api from "@/lib/api";
import { toast } from "sonner";

const COUNTRIES = [
  "Canada", "United States", "Mexico", "United Kingdom", "Ireland", "France", "Germany",
  "Spain", "Italy", "Netherlands", "Belgium", "Switzerland", "Sweden", "Norway",
  "Australia", "New Zealand", "United Arab Emirates", "Saudi Arabia", "Qatar", "Kuwait",
  "India", "Pakistan", "Bangladesh", "Singapore", "Malaysia", "Indonesia", "Japan", "China",
  "Egypt", "Morocco", "Nigeria", "South Africa", "Kenya", "Brazil", "Argentina", "Other",
];
const CA_PROVINCES = [
  ["ON", "Ontario"], ["AB", "Alberta"], ["BC", "British Columbia"], ["MB", "Manitoba"],
  ["NB", "New Brunswick"], ["NL", "Newfoundland and Labrador"], ["NS", "Nova Scotia"],
  ["NT", "Northwest Territories"], ["NU", "Nunavut"], ["PE", "Prince Edward Island"],
  ["QC", "Quebec"], ["SK", "Saskatchewan"], ["YT", "Yukon"],
];

const field = "w-full bg-white border border-[#d8d1c4] rounded-md px-4 py-2.5 text-sm outline-none focus:ring-1 focus:ring-black";

// Fires the Meta Pixel standard "Purchase" event exactly once per completed order,
// using real order data returned by the backend capture (no hard-coded values).
function firePurchasePixel(purchase) {
  if (!purchase || !purchase.order_id) return;
  try {
    const guardKey = `fb_purchase_fired_${purchase.order_id}`;
    if (localStorage.getItem(guardKey)) return; // already tracked this order
    if (typeof window.fbq !== "function") return;
    window.fbq("track", "Purchase", {
      value: purchase.value,
      currency: purchase.currency,
      contents: purchase.contents,
      content_ids: purchase.content_ids,
      content_type: purchase.content_type || "product",
      num_items: purchase.num_items,
    });
    localStorage.setItem(guardKey, "1");
  } catch (e) {
    // never let analytics break checkout
  }
}

export default function Checkout() {
  const { items, subtotal, clear, discountCode } = useCart();
  const { format: money, currency } = useCurrency();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [addr, setAddr] = useState({ name: "", email: "", phone: "", address: "", city: "", country: "Canada", province: "ON", postal: "" });
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [orderNotes, setOrderNotes] = useState("");
  const debounceRef = useRef(null);

  const set = (k, v) => setAddr((a) => ({ ...a, [k]: v }));
  const isCanada = addr.country === "Canada";

  useEffect(() => {
    api.get("/paypal/config").then((r) => setConfig(r.data)).catch(() => setConfig({ configured: false }));
  }, []);

  const fetchQuote = useCallback(() => {
    if (items.length === 0 || !addr.country) return;
    setQuoting(true);
    api.post("/checkout/quote", {
      items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, color: i.color, size: i.size })),
      discount_code: discountCode || null,
      country: addr.country,
      province: isCanada ? addr.province : null,
      postal_code: addr.postal || null,
      currency,
    })
      .then((r) => setQuote(r.data))
      .catch(() => setQuote(null))
      .finally(() => setQuoting(false));
  }, [items, discountCode, addr.country, addr.province, addr.postal, isCanada, currency]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchQuote, 350);
    return () => clearTimeout(debounceRef.current);
  }, [fetchQuote]);

  const emailValid = /\S+@\S+\.\S+/.test(addr.email);
  const addressValid = addr.name.trim() && emailValid && addr.address.trim() && addr.city.trim() &&
    addr.country && addr.postal.trim() && (!isCanada || addr.province);

  if (items.length === 0) {
    return (
      <div className="container-lira py-32 text-center" data-testid="checkout-empty">
        <h1 className="font-serif text-4xl md:text-5xl font-light">Your bag is empty</h1>
        <Link to="/collection/all" className="btn-primary mt-10 inline-flex">Shop the Collection</Link>
      </div>
    );
  }

  return (
    <div className="container-lira py-14 md:py-20" data-testid="checkout-page">
      <Link to="/cart" className="inline-flex items-center gap-2 text-sm text-[#737373] hover:text-[#1A1A1A] mb-6" data-testid="back-to-cart">
        <ChevronLeft strokeWidth={1} className="h-4 w-4" /> Back to bag
      </Link>
      <h1 className="font-serif text-4xl md:text-6xl font-light tracking-tight mb-12">Checkout</h1>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-12">
        {/* Shipping address */}
        <div>
          <h2 className="eyebrow mb-6">Shipping Address</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="text-xs text-[#737373] mb-1 block">Full name</label>
              <input value={addr.name} onChange={(e) => set("name", e.target.value)} className={field} data-testid="checkout-name" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-[#737373] mb-1 block">Email</label>
              <input type="email" value={addr.email} onChange={(e) => set("email", e.target.value)} className={field} data-testid="checkout-email" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-[#737373] mb-1 block">Phone number</label>
              <input type="tel" value={addr.phone} onChange={(e) => set("phone", e.target.value)} className={field} placeholder="For delivery updates" data-testid="checkout-phone" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-[#737373] mb-1 block">Street address</label>
              <input value={addr.address} onChange={(e) => set("address", e.target.value)} className={field} data-testid="checkout-address" />
            </div>
            <div>
              <label className="text-xs text-[#737373] mb-1 block">City</label>
              <input value={addr.city} onChange={(e) => set("city", e.target.value)} className={field} data-testid="checkout-city" />
            </div>
            <div>
              <label className="text-xs text-[#737373] mb-1 block">Country</label>
              <select value={addr.country} onChange={(e) => set("country", e.target.value)} className={field} data-testid="checkout-country">
                {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-[#737373] mb-1 block">{isCanada ? "Province" : "State / Region"}</label>
              {isCanada ? (
                <select value={addr.province} onChange={(e) => set("province", e.target.value)} className={field} data-testid="checkout-province">
                  {CA_PROVINCES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
                </select>
              ) : (
                <input value={addr.province} onChange={(e) => set("province", e.target.value)} className={field} placeholder="State / region" data-testid="checkout-state" />
              )}
            </div>
            <div>
              <label className="text-xs text-[#737373] mb-1 block">Postal / ZIP code</label>
              <input value={addr.postal} onChange={(e) => set("postal", e.target.value.toUpperCase())} className={field} data-testid="checkout-postal" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-[#737373] mb-1 block">Order notes <span className="text-[#a8a29a]">optional</span></label>
              <textarea value={orderNotes} onChange={(e) => setOrderNotes(e.target.value)} rows={3} className={field}
                placeholder="Anything we should know? e.g. gift note, delivery instructions, colour preference…" data-testid="checkout-order-notes" />
            </div>
          </div>
        </div>

        {/* Order summary */}
        <aside className="bg-beige rounded-sm p-8 h-fit">
          <h2 className="eyebrow mb-6">Order Summary</h2>

          <div className="space-y-4 max-h-64 overflow-auto pr-1 mb-6">
            {items.map((i) => (
              <div key={`${i.product_id}-${i.color}-${i.size}`} className="flex gap-3" data-testid="checkout-line-item">
                <div className="w-14 aspect-[3/4] bg-white rounded-sm overflow-hidden shrink-0">
                  <img src={i.image} alt={i.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 text-sm">
                  <div className="font-serif">{i.name}</div>
                  <div className="text-xs text-[#737373]">{[i.size, i.color].filter(Boolean).join(" · ")} · Qty {i.quantity}</div>
                </div>
                <div className="text-sm">{money(i.price * i.quantity)}</div>
              </div>
            ))}
          </div>

          <div className="space-y-3 text-sm border-t border-[#d8d1c4] pt-5">
            <div className="flex justify-between"><span className="text-[#5a5a5a]">Subtotal</span><span data-testid="summary-subtotal">{money(quote ? quote.subtotal : subtotal)}</span></div>
            {quote && quote.discount_amount > 0 && (
              <div className="flex justify-between text-[#3a6b45]" data-testid="checkout-discount-line"><span>Discount ({discountCode})</span><span>−{money(quote.discount_amount)}</span></div>
            )}
            {quote && quote.discount_error && discountCode && (
              <p className="text-sm text-[#9a3b3b]" data-testid="checkout-discount-error">
                {quote.discount_reason === "below_min" && currency !== "CAD" && quote.discount_min
                  ? `Error: for this code to be applied, your cart's subtotal must be ${money(quote.discount_min)}+`
                  : quote.discount_error}
              </p>
            )}

            <div className="flex justify-between">
              <span className="text-[#5a5a5a]">Shipping</span>
              <span data-testid="summary-shipping">{quoting ? "…" : quote ? (quote.free_shipping ? "Free" : money(quote.shipping)) : "—"}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-[#5a5a5a]">Tax {quote && quote.tax_rate > 0 ? `(${(quote.tax_rate * 100).toFixed(quote.tax_rate === 0.14975 ? 3 : 0)}%)` : ""}</span>
              <span data-testid="summary-tax">{quoting ? "…" : quote ? money(quote.tax) : "—"}</span>
            </div>

            {quote && quote.duties > 0 && (
              <div className="flex justify-between">
                <span className="text-[#5a5a5a]">Duties</span>
                <span data-testid="summary-duties">{money(quote.duties)}</span>
              </div>
            )}

            <div className="flex justify-between text-lg pt-3 border-t border-[#d8d1c4]">
              <span>Total ({currency})</span>
              <span data-testid="summary-total">{quoting ? "…" : quote ? money(quote.total) : "—"}</span>
            </div>

            {!isCanada && (
              <p className="text-xs text-[#3a6b45] leading-relaxed pt-1" data-testid="duties-notice">
                Duties are included and paid now — you won't be charged customs or duties when your package arrives.
              </p>
            )}
          </div>

          {/* Shipping estimator details */}
          {quote && (
            <div className="mt-5 bg-white rounded-md p-4 text-xs text-[#5a5a5a] space-y-1.5" data-testid="shipping-estimate">
              <div className="flex items-center gap-2 text-[#1A1A1A] font-medium"><Truck strokeWidth={1.25} className="h-4 w-4" /> {quote.shipping_method}</div>
              <div>Estimated delivery: <strong>{quote.shipping_delivery}</strong></div>
              <div className="flex items-center gap-2"><Package strokeWidth={1.25} className="h-3.5 w-3.5" /> Package weight: {quote.weight_g} g ({quote.weight_kg} kg)</div>
              {isCanada && !quote.free_shipping && quote.subtotal < 150 && (
                <div className="text-[#8a6d3b]">Add {money(150 - quote.subtotal)} more for free shipping within Canada.</div>
              )}
            </div>
          )}

          {/* Payment */}
          <div className="mt-6">
            {!config ? (
              <div className="h-12 rounded bg-white/60 animate-pulse" />
            ) : !config.configured ? (
              <div className="text-sm text-[#8a6d3b] bg-white border border-[#e6dcc4] rounded-md p-4">Payment will be enabled once credentials are added.</div>
            ) : !addressValid ? (
              <div className="text-center text-sm text-[#8a6d3b] bg-white border border-[#e6dcc4] rounded-md p-4" data-testid="fill-address-notice">
                Please complete your shipping address to continue to payment.
              </div>
            ) : (
              <div data-testid="paypal-buttons">
                <PayPalScriptProvider options={{ clientId: config.client_id, currency: currency, intent: "capture" }}>
                  <PayPalButtons
                    forceReRender={[quote?.total, currency]}
                    style={{ layout: "vertical", shape: "rect", color: "black", label: "paypal" }}
                    disabled={quoting || !quote}
                    createOrder={async () => {
                      const res = await api.post("/paypal/orders", {
                        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, color: i.color, size: i.size })),
                        discount_code: discountCode || null,
                        country: addr.country,
                        province: isCanada ? addr.province : addr.province,
                        postal_code: addr.postal,
                        ship_name: addr.name,
                        address: addr.address,
                        city: addr.city,
                        email: addr.email,
                        phone: addr.phone,
                        currency,
                        order_notes: orderNotes || null,
                      });
                      return res.data.order_id;
                    }}
                    onApprove={async (data) => {
                      try {
                        const res = await api.post(`/paypal/orders/${data.orderID}/capture`);
                        if (res.data.payment_status === "paid") {
                          firePurchasePixel(res.data.purchase);
                          clear();
                          navigate("/checkout/success?status=paid");
                        } else {
                          toast.error("Payment could not be completed.");
                        }
                      } catch {
                        toast.error("Payment capture failed. Please try again.");
                      }
                    }}
                    onError={() => toast.error("Something went wrong with PayPal. Please try again.")}
                  />
                </PayPalScriptProvider>
              </div>
            )}
          </div>

          <p className="flex items-center justify-center gap-2 text-xs text-[#737373] mt-4"><Lock strokeWidth={1} className="h-3.5 w-3.5" /> Secure payment via PayPal · Charged in {currency}</p>
        </aside>
      </div>
    </div>
  );
}
